import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, fetchAuthedBlobUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Calendar, Plus, Star, Clock, Trash2, Pencil, ShieldCheck, ShieldAlert, FileText } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import ReferralCard from "../components/ReferralCard";
import ReviewDialog from "../components/ReviewDialog";
import KycPanel from "../components/KycPanel";
import BookingEditDialog from "../components/BookingEditDialog";
import Seo from "../components/Seo";

export default function LearnerDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null); // booking object
  const [reviewedIds, setReviewedIds] = useState(new Set());
  const [overtimeBusy, setOvertimeBusy] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [tab, setTab] = useState("bookings");

  const load = () => {
    setLoading(true);
    api.get("/learner/bookings").then(r => setItems(r.data)).finally(() => setLoading(false));
    api.get("/learner/kyc").then(r => setKycStatus(r.data.kyc_status)).catch(() => setKycStatus(null));
    // Only returns materials from trainers this learner has actually paid.
    api.get("/learner/materials").then(r => setMaterials(r.data)).catch(() => setMaterials([]));
  };
  useEffect(() => { load(); }, []);

  // Overtime never reaches the trainer's payout balance until the learner
  // approves it here and the second payment settles.
  const approveOvertime = async (bookingId) => {
    setOvertimeBusy(bookingId);
    try {
      const { data } = await api.post("/payments/overtime-checkout", {
        booking_id: bookingId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start overtime payment");
      setOvertimeBusy(null);
    }
  };

  const declineOvertime = async (bookingId) => {
    if (!window.confirm("Decline the extra time your instructor logged? They will not be paid for it.")) return;
    setOvertimeBusy(bookingId);
    try {
      await api.post(`/bookings/${bookingId}/overtime/decline`);
      toast.success("Overtime declined");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not decline");
    } finally {
      setOvertimeBusy(null);
    }
  };

  // Only unpaid, not-yet-started bookings can be removed — the backend
  // enforces the same rule, this just keeps the button off paid rows.
  const canDelete = (b) =>
    b.payment_status !== "paid" && !["started", "completed"].includes(b.status);

  // Same gate as delete: unpaid and not yet under way.
  const canEdit = (b) => canDelete(b);

  const saveEdit = async (payload) => {
    try {
      await api.patch(`/bookings/${editing.booking_id}`, payload);
      toast.success("Booking updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update this booking");
    }
  };

  const materialFor = (trainerId) => materials.find(m => m.trainer_id === trainerId);

  const downloadMaterial = async (material) => {
    try {
      const url = await fetchAuthedBlobUrl(`/materials/${material.material_id}/download`);
      const a = document.createElement("a");
      a.href = url;
      a.download = material.original_filename || "cbt-test.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not download the material");
    }
  };

  const deleteBooking = async (b) => {
    if (!window.confirm(`Delete this unpaid booking with ${b.trainer_name || "your instructor"}? This cannot be undone.`)) return;
    setDeletingId(b.booking_id);
    try {
      const { data } = await api.delete(`/bookings/${b.booking_id}`);
      const returned = Number(data?.credits_returned || 0);
      toast.success(returned > 0
        ? `Booking deleted — $${returned.toFixed(2)} in credits returned`
        : "Booking deleted");
      setItems(prev => prev.filter(i => i.booking_id !== b.booking_id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete this booking");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="learner-dashboard">
      <Seo title="My Dashboard – DriveTerrain" description="Your DriveTerrain learner dashboard." canonical="/learner" robots="noindex, nofollow" hrefLangs={false} />
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary">Learner Dashboard</div>
          <h1 className="text-4xl font-display font-semibold tracking-tight mt-1">My bookings</h1>
        </div>
        <Link to="/trainers"><Button className="rounded-sm" data-testid="new-booking-btn"><Plus className="w-4 h-4 mr-2" /> New booking</Button></Link>
        <Link to="/cbt"><Button variant="outline" className="rounded-sm" data-testid="cbt-link"><Star className="w-4 h-4 mr-2" /> CBT Practice</Button></Link>
      </div>

      {/* Verification is informational — an unverified learner can still book. */}
      {kycStatus && kycStatus !== "verified" && (
        <div className="border border-primary/40 bg-primary/5 p-4 mb-8 flex flex-wrap items-center justify-between gap-3" data-testid="kyc-warning-banner">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-medium">
                {kycStatus === "pending" ? "Your ID is under review" :
                 kycStatus === "rejected" ? "Your ID needs attention" :
                 "Your identity isn't verified yet"}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                You can book lessons as normal — instructors will just see that your ID is unverified until an admin approves it.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setTab("verification")} data-testid="go-to-verification">
            {kycStatus === "incomplete" ? "Verify my ID" : "View status"}
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm max-w-full overflow-x-auto no-scrollbar justify-start">
          <TabsTrigger value="bookings" data-testid="tab-learner-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="verification" data-testid="tab-learner-verification">
            Verification
            {kycStatus === "verified" && <ShieldCheck className="w-3.5 h-3.5 ml-1.5 text-primary" />}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="bookings" className="mt-6">
      <div className="mb-8">
        <ReferralCard />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
        {[
          { l: "Total bookings", v: items.length },
          { l: "Upcoming", v: items.filter(i => ["paid", "pending_payment"].includes(i.status)).length },
          { l: "Completed", v: items.filter(i => i.status === "completed").length },
          { l: "Total spent", v: `$${items.filter(i => i.payment_status === "paid").reduce((s, i) => s + i.amount + (i.overtime_status === "settled" ? (i.overtime_amount || 0) : 0), 0).toFixed(2)}` },
        ].map((s) => (
          <div key={s.l} className="border border-border bg-card p-4 sm:p-5 min-w-0">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{s.l}</div>
            <div className="font-display text-2xl sm:text-3xl font-semibold mt-1 truncate">{s.v}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-border bg-card p-12 text-center" data-testid="no-bookings">
          <Calendar className="w-10 h-10 mx-auto text-muted-foreground" />
          <h3 className="font-display text-xl font-medium mt-4">No bookings yet</h3>
          <p className="text-muted-foreground text-sm mt-2">Find a trainer near you to book your first session.</p>
          <Link to="/trainers"><Button className="rounded-sm mt-6">Browse trainers</Button></Link>
        </div>
      ) : (
        <div className="border border-border bg-card">
          <div className="hidden md:grid md:grid-cols-6 px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
            <div className="col-span-2">Trainer</div>
            <div>Type</div>
            <div>Amount</div>
            <div>Status</div>
            <div className="text-right">Action</div>
          </div>
          {items.map(b => (
            <div key={b.booking_id} className="grid grid-cols-1 gap-2 md:grid-cols-6 md:gap-0 px-4 sm:px-5 py-4 md:items-start border-b border-border last:border-0 text-sm hover:bg-muted/40" data-testid={`booking-row-${b.booking_id}`}>
              <div className="md:col-span-2">
                <div className="font-medium">{b.trainer_name || "—"}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Booked {new Date(b.created_at).toLocaleString()}
                  {b.scheduled_at && <> · Lesson {new Date(b.scheduled_at).toLocaleString()}</>}
                  {b.paid_at && <> · Paid {new Date(b.paid_at).toLocaleDateString()}</>}
                </div>
                {b.overtime_status === "pending_learner_approval" && (
                  <div className="mt-3 border border-primary/40 bg-primary/5 p-3" data-testid={`overtime-request-${b.booking_id}`}>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary font-bold">
                      <Clock className="w-3 h-3" /> Overtime awaiting your approval
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Your instructor logged <span className="font-semibold text-foreground">{b.overtime_minutes} extra minutes</span>
                      {" "}(<span className="font-semibold text-foreground">${(b.overtime_amount || 0).toFixed(2)}</span>).
                      Nothing has been charged yet.
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" className="rounded-sm text-xs" disabled={overtimeBusy === b.booking_id}
                        onClick={() => approveOvertime(b.booking_id)}
                        data-testid={`approve-overtime-${b.booking_id}`}>
                        {overtimeBusy === b.booking_id ? "Opening…" : "Approve & pay"}
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-sm text-xs" disabled={overtimeBusy === b.booking_id}
                        onClick={() => declineOvertime(b.booking_id)}
                        data-testid={`decline-overtime-${b.booking_id}`}>
                        Decline
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div>{b.booking_type}</div>
              <div className="font-medium">${b.amount.toFixed(2)}</div>
              <div>
                <span className={`inline-block px-2 py-0.5 text-xs uppercase tracking-widest border ${
                  b.status === "completed" ? "border-foreground/30" :
                  b.status === "paid" || b.status === "started" ? "border-primary text-primary" :
                  "border-border text-muted-foreground"
                }`}>{b.status}</span>
              </div>
              <div className="flex flex-wrap justify-end items-start gap-2">
                {b.payment_status !== "paid" && <Link to={`/booking/${b.booking_id}`}><Button size="sm" variant="outline" className="rounded-sm" data-testid={`pay-${b.booking_id}`}>Pay</Button></Link>}
                {b.payment_status === "paid" && b.status !== "completed" && <Link to={`/session/${b.booking_id}`}><Button size="sm" className="rounded-sm" data-testid={`session-${b.booking_id}`}>Session</Button></Link>}
                {b.payment_status === "paid" && materialFor(b.trainer_id) && (
                  <Button size="sm" variant="outline" className="rounded-sm"
                    onClick={() => downloadMaterial(materialFor(b.trainer_id))}
                    data-testid={`download-material-${b.booking_id}`}>
                    <FileText className="w-3 h-3 mr-1" /> Download Training Material
                  </Button>
                )}
                {b.status === "completed" && !reviewedIds.has(b.booking_id) && (
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setReviewing(b)} data-testid={`review-${b.booking_id}`}>
                    <Star className="w-3 h-3 mr-1" /> Review
                  </Button>
                )}
                {canEdit(b) && (
                  <Button size="sm" variant="ghost"
                    className="rounded-sm text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                    onClick={() => setEditing(b)}
                    aria-label="Edit booking"
                    data-testid={`edit-booking-${b.booking_id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                )}
                {canDelete(b) && (
                  <Button size="sm" variant="ghost" disabled={deletingId === b.booking_id}
                    className="rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    onClick={() => deleteBooking(b)}
                    aria-label="Delete booking"
                    data-testid={`delete-booking-${b.booking_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
        </TabsContent>

        <TabsContent value="verification" className="mt-6">
          <KycPanel audience="learner" />
        </TabsContent>
      </Tabs>

      {editing && (
        <BookingEditDialog
          booking={editing}
          onOpenChange={(o) => !o && setEditing(null)}
          onSave={saveEdit}
        />
      )}

      {reviewing && (
        <ReviewDialog
          open={!!reviewing}
          onOpenChange={(o) => !o && setReviewing(null)}
          bookingId={reviewing.booking_id}
          trainerName={reviewing.trainer_name}
          onSubmitted={() => { setReviewedIds(s => new Set([...s, reviewing.booking_id])); load(); }}
        />
      )}
    </div>
  );
}
